/**
 * src/shell/gallery/ArtworkCard.tsx
 *
 * Responsive, Accessible Artwork Card with Lazy Image Loading,
 * COA Status Pill, and Private Price Masking.
 */

import React, { useState } from 'react';
import { ArtworkProduct } from '../../domain/inventory/types/product-types';

interface ArtworkCardProps {
  artwork: ArtworkProduct;
  onInspect: (artwork: ArtworkProduct) => void;
  onEdit?: (artwork: ArtworkProduct) => void;
}

export const ArtworkCard: React.FC<ArtworkCardProps> = ({ artwork, onInspect, onEdit }) => {
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);

  const primaryImage = artwork.metadata.images.find((img) => img.isPrimary) || artwork.metadata.images[0];
  const hasCoa = !!artwork.metadata.certificateOfAuthenticity;
  const isCoaVerified = artwork.metadata.certificateOfAuthenticity?.isVerified;

  return (
    <div
      style={{
        backgroundColor: 'var(--theme-bg-card, #151d2a)',
        borderRadius: '12px',
        border: '1px solid var(--theme-border, #1e293b)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
        transition: 'transform 0.2s ease, border-color 0.2s ease',
      }}
    >
      {/* Media Viewport */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          paddingTop: '75%', // 4:3 Aspect Ratio
          backgroundColor: '#090d16',
          cursor: 'pointer',
        }}
        onClick={() => onInspect(artwork)}
      >
        {!imgLoaded && !imgError && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#1e293b',
              color: '#64748b',
              fontSize: '12px',
            }}
          >
            Loading artwork...
          </div>
        )}

        {imgError ? (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#0f172a',
              color: '#ef4444',
              fontSize: '12px',
            }}
          >
            Image Unavailable
          </div>
        ) : (
          <img
            src={primaryImage?.url}
            alt={artwork.title}
            loading="lazy"
            decoding="async"
            onLoad={() => setImgLoaded(true)}
            onError={() => setImgError(true)}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: primaryImage?.fitMode === 'cover' ? 'cover' : 'contain',
              opacity: imgLoaded ? 1 : 0,
              transition: 'opacity 0.3s ease',
            }}
          />
        )}

        {/* Asset Count Pill Overlay */}
        {artwork.metadata.images.length > 1 && (
          <div
            style={{
              position: 'absolute',
              bottom: '10px',
              left: '10px',
              fontSize: '11px',
              fontWeight: 600,
              padding: '2px 8px',
              borderRadius: '6px',
              backgroundColor: 'rgba(15, 23, 42, 0.75)',
              color: '#f8fafc',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(4px)',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            📷 1/{artwork.metadata.images.length}
          </div>
        )}

        {/* COA Badge Overlay */}
        <div style={{ position: 'absolute', top: '10px', right: '10px', display: 'flex', gap: '6px' }}>
          {hasCoa && (
            <span
              style={{
                fontSize: '10px',
                fontWeight: 700,
                padding: '3px 8px',
                borderRadius: '6px',
                backgroundColor: isCoaVerified ? 'rgba(52, 211, 153, 0.2)' : 'rgba(245, 158, 11, 0.2)',
                color: isCoaVerified ? '#34d399' : '#f59e0b',
                border: `1px solid ${isCoaVerified ? '#34d399' : '#f59e0b'}`,
                backdropFilter: 'blur(4px)',
              }}
            >
              {isCoaVerified ? '🔐 Verified COA' : '📜 Attached COA'}
            </span>
          )}
        </div>
      </div>

      {/* Card Content */}
      <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', flex: 1 }}>
        <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--theme-text-primary, #f8fafc)', marginBottom: '4px' }}>
          {artwork.title}
        </div>
        <div style={{ fontSize: '13px', color: 'var(--theme-accent, #38bdf8)', fontWeight: 600, marginBottom: '8px' }}>
          {artwork.metadata.artist}
        </div>
        <div style={{ fontSize: '12px', color: 'var(--theme-text-secondary, #94a3b8)', marginBottom: '12px' }}>
          {artwork.metadata.medium} ({artwork.metadata.yearCreated})
        </div>

        <div
            style={{
              fontSize: '12px',
              color: artwork.description ? 'var(--theme-text-secondary, #94a3b8)' : '#64748b',
              fontStyle: artwork.description ? 'normal' : 'italic',
              lineHeight: '1.4',
              marginBottom: '12px',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {artwork.description || 'No description provided.'}
          </div>

        <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '12px', borderTop: '1px solid var(--theme-border, #1e293b)' }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: artwork.price.isPrivate ? '#a855f7' : '#34d399' }}>
            {artwork.price.formatted}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {onEdit && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(artwork);
                }}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  backgroundColor: 'rgba(56, 189, 248, 0.1)',
                  color: 'var(--theme-accent, #38bdf8)',
                  border: '1px solid rgba(56, 189, 248, 0.3)',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Edit
              </button>
            )}
            <button
              onClick={() => onInspect(artwork)}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                backgroundColor: 'var(--theme-accent, #38bdf8)',
                color: '#090d16',
                border: 'none',
                fontSize: '12px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Inspect
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
